/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, TrendingUp, Calendar, Sparkles, Activity, Users } from 'lucide-react';
import EcosystemBadge from '@/components/terra/EcosystemBadge';

export default function CentreGrowthDashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const cId = user?.centre_id ? `?centre_id=${user.centre_id}` : '';
        const [s, e] = await Promise.all([
          axios.get(`${API}/terra-growth/centre-dashboard/summary${cId}`),
          axios.get(`${API}/terra-growth/ecosystems`),
        ]);
        setSummary(s.data); setEcosystems(e.data?.ecosystems || []);
      } catch { /* */ } finally { setLoading(false); }
    })();
  }, [user]);

  const ecoMap = ecosystems.reduce((m, e) => { m[e.code] = e; return m; }, {});

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="centre-growth-dashboard" className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Layer 9</p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Centre Growth Dashboard</h1>
          <p className="text-sm text-[#8A8681] mt-1 max-w-xl">
            Territory pulse for {summary?.centre_id || 'your centre'} — perception movement, depth mix, right-subscriber emergence, event impact.
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading…
          </div>
        ) : !summary ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            Could not load summary.
          </div>
        ) : (
          <>
            <div data-testid="centre-metric-strip" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Metric testId="centre-metric-emergence" icon={<Sparkles className="w-4 h-4" />}
                      label="Right subscriber emergence" value={summary.right_subscriber_emergence} />
              <Metric testId="centre-metric-deep-share" icon={<TrendingUp className="w-4 h-4" />}
                      label="Deep conversation share · 7d"
                      value={`${summary.depth_mix?.deep_share_pct ?? 0}%`}
                      sub={`${summary.depth_mix?.total ?? 0} logs total`} />
              <Metric testId="centre-metric-perception-7d" icon={<Activity className="w-4 h-4" />}
                      label="Perception movements · 7d" value={summary.perception_movements_7d} />
              <Metric testId="centre-metric-impact-avg" icon={<Calendar className="w-4 h-4" />}
                      label="Avg event impact"
                      value={summary.events?.avg_impact_composite ?? '—'}
                      sub={`${(summary.events?.recent_closed || []).length} closed`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section testId="centre-perception" title="Perception distribution"
                       sub="Where this territory sits across the 12-state ladder.">
                <PerceptionBars distribution={summary.perception_distribution || {}}
                                labels={summary.perception_labels || {}} total={summary.depth_mix?.total} />
              </Section>

              <Section testId="centre-depth" title="Conversation depth · this week"
                       sub="Pipelines roll up by depth, not call count.">
                <DepthBars buckets={summary.depth_mix?.buckets || {}} />
              </Section>

              <Section testId="centre-upcoming" title="Upcoming events"
                       sub="Each event will be measured by Perception Impact Score, not attendance.">
                {(summary.events?.upcoming || []).length === 0 ? (
                  <p className="text-xs text-[#8A8681]">No upcoming events.</p>
                ) : (summary.events.upcoming || []).map(e => (
                  <div key={e.id} data-testid={`centre-upcoming-${e.id}`}
                       className="flex items-center justify-between gap-3 rounded-xl border border-[#EBE8E3] px-3 py-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm text-[#2C2A29] truncate">{e.title}</div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8681]">
                        {e.event_type.replace(/_/g, ' ')}
                      </div>
                    </div>
                    <span className="text-[10px] text-[#5C5955] whitespace-nowrap">
                      {new Date(e.scheduled_for).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </Section>

              <Section testId="centre-ecosystems" title="Top ecosystems"
                       sub="Where your right subscribers are entering from.">
                {(summary.top_ecosystems || []).length === 0 ? (
                  <p className="text-xs text-[#8A8681]">No ecosystem-tagged leads yet.</p>
                ) : summary.top_ecosystems.map(e => (
                  <div key={e.code} data-testid={`centre-eco-${e.code}`}
                       className="flex items-center justify-between rounded-xl border border-[#EBE8E3] px-3 py-2 mb-2">
                    <EcosystemBadge ecosystem={ecoMap[e.code] || { code: e.code, name: e.code }} />
                    <span className="text-sm tabular-nums text-[#2C2A29]">{e.count}</span>
                  </div>
                ))}
              </Section>
            </div>
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function Metric({ testId, icon, label, value, sub }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">
        {icon} {label}
      </div>
      <div className="text-3xl font-['Outfit'] text-[#2C2A29] tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[11px] text-[#B5B0A8]">{sub}</div>}
    </div>
  );
}

function Section({ testId, title, sub, children }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <h2 className="text-base font-['Outfit'] text-[#2C2A29]">{title}</h2>
      <p className="text-[11px] text-[#8A8681] mb-4">{sub}</p>
      {children}
    </section>
  );
}

function PerceptionBars({ distribution, labels }) {
  const entries = Object.entries(distribution).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return <p className="text-xs text-[#8A8681]">No leads tagged yet.</p>;
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <ul className="space-y-2">
      {entries.map(([state, count]) => {
        const pct = Math.round((count / total) * 100);
        return (
          <li key={state} className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#5C5955] w-44 truncate">
              {labels[state] || state}
            </span>
            <div className="flex-1 h-2 rounded-full bg-[#F4F2EE] overflow-hidden">
              <div className="h-full bg-[#4A5D4E]" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] tabular-nums text-[#5C5955] w-10 text-right">{count}</span>
          </li>
        );
      })}
    </ul>
  );
}

function DepthBars({ buckets }) {
  const order = ['SUPERFICIAL', 'EXPLORATORY', 'REFLECTIVE', 'REALIZATION_LEVEL',
                 'DECISION_LEVEL', 'FAMILY_ALIGNMENT', 'CONVERSION_LEVEL'];
  const total = order.reduce((s, k) => s + (buckets[k] || 0), 0) || 1;
  return (
    <ul className="space-y-1.5">
      {order.map(k => {
        const v = buckets[k] || 0;
        const pct = Math.round((v / total) * 100);
        return (
          <li key={k} className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#5C5955] w-44 truncate">{k.replace(/_/g, ' ').toLowerCase()}</span>
            <div className="flex-1 h-2 rounded-full bg-[#F4F2EE] overflow-hidden">
              <div className="h-full bg-[#8C6239]" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] tabular-nums text-[#5C5955] w-10 text-right">{v}</span>
          </li>
        );
      })}
    </ul>
  );
}
